"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { Bookmark } from "lucide-react";
import { useT } from "@/hooks/useT";
import { sourceUrl, type Source } from "@/lib/source-url";
import { BookmarkIcon } from "@/app/components/icons/BookmarkIcon";
import { BookmarkXIcon } from "@/app/components/icons/BookmarkXIcon";
import type { Event } from "../types";

// Lazy — same loader the parent had inline. ExplosionEffect is only
// referenced when a card is actively being un-bookmarked.
const ExplosionEffect = dynamic(() => import("@/app/components/ExplosionEffect"), { ssr: false });

interface MiniCardExplosion {
  slug: string;
  rect: DOMRect;
}

interface BookmarksMiniGridProps {
  loadingWatching: boolean;
  watchingEvents: Event[];
  /** Just the count — sized to the parent's WatchlistItem[] without coupling. */
  watchlistCount: number;
  newsSidebarOpen: boolean;
  source: Source;
  miniCardExplosion: MiniCardExplosion | null;
  setMiniCardExplosion: (e: MiniCardExplosion | null) => void;
  bookmarkHoveredSlug: string | null;
  setBookmarkHoveredSlug: (slug: string | null) => void;
  toggleWatchlist: (slug: string) => void;
  /**
   * Returns the live YES price for a market — `useRealtimeMarketPrice`
   * lookup that the parent already memoizes. We pass the function in so
   * the per-card hook subscriptions stay attached to the parent
   * component's lifecycle.
   */
  getRealtimePrice: (market: any) => [number, number];
}

/**
 * Compact "Bookmarks" grid shown at the top of the For-You category.
 *
 * Pulled out of MarketsPageContent.tsx where it was a 130-line block of
 * JSX nested inside a category branch. Twelve props sounds like a lot
 * but every dep is already stable in the parent (callbacks are
 * useCallback'd, refs are stable, state setters never change identity),
 * so the move doesn't introduce any new re-render concerns.
 *
 * Renders one of three states:
 *   - skeleton (loading + watchlist non-empty)
 *   - grid (watchingEvents.length > 0)
 *   - nothing (no bookmarks yet)
 *
 * The parent decides when to mount this; the component itself just
 * renders the appropriate sub-state based on its props.
 */
export function BookmarksMiniGrid({
  loadingWatching,
  watchingEvents,
  watchlistCount,
  newsSidebarOpen,
  source,
  miniCardExplosion,
  setMiniCardExplosion,
  bookmarkHoveredSlug,
  setBookmarkHoveredSlug,
  toggleWatchlist,
  getRealtimePrice,
}: BookmarksMiniGridProps) {
  const t = useT();

  if (watchingEvents.length === 0 && !(loadingWatching && watchlistCount > 0)) {
    return null;
  }

  // Loading skeleton — shown only while initial fetch is in flight and we
  // know from `watchlist` that bookmarks exist server-side.
  if (loadingWatching && watchingEvents.length === 0 && watchlistCount > 0) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-monument font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-[#7C3AED] dark:text-[#CCFF00] fill-current" />
            {t("forYou.bookmarks")}
          </h3>
        </div>
        <div className={`grid grid-cols-2 sm:grid-cols-3 ${!newsSidebarOpen ? "lg:grid-cols-4 xl:grid-cols-5" : "lg:grid-cols-3 xl:grid-cols-4"} gap-2`}>
          {Array.from({ length: Math.min(watchlistCount, 6) }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-white/[0.04] border border-gray-200/80 dark:border-[#1A1A1A] rounded-[5px] p-3 animate-pulse">
              <div className="w-full h-10 bg-gray-200 dark:bg-white/[0.06] rounded-[5px] mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-white/[0.06] rounded w-3/4 mb-1" />
              <div className="h-3 bg-gray-200 dark:bg-white/[0.06] rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-monument font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Bookmark className="w-5 h-5 text-[#7C3AED] dark:text-[#CCFF00] fill-current" />
          {t("forYou.bookmarks")}
        </h3>
        <Link
          href={sourceUrl("/markets/bookmarks", source)}
          className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-satoshi transition-colors"
        >
          {t("forYou.viewAll")}
        </Link>
      </div>
      <div className={`grid grid-cols-2 sm:grid-cols-3 ${!newsSidebarOpen ? "lg:grid-cols-4 xl:grid-cols-5" : "lg:grid-cols-3 xl:grid-cols-4"} gap-2`}>
        {watchingEvents.slice(0, 10).map((event) => {
          const mainMarket = event.markets?.[0];
          if (!mainMarket) return null;
          const [yesPrice] = getRealtimePrice(mainMarket);
          const marketsCount = (event as any).marketsCount ?? event.markets.length;
          const vol = event.volume24hr || event.volume || 0;
          const volLabel = vol >= 1_000_000
            ? `$${(vol / 1_000_000).toFixed(1)}M`
            : vol >= 1_000
              ? `$${(vol / 1_000).toFixed(0)}K`
              : `$${vol.toFixed(0)}`;
          const isLive = event.active && !event.closed;

          return (
            <motion.div
              key={event.slug}
              animate={miniCardExplosion?.slug === event.slug
                ? { scale: [1, 0.95, 0], opacity: [1, 1, 0], rotate: [0, -1, 3] }
                : { scale: 1, opacity: 1 }
              }
              transition={miniCardExplosion?.slug === event.slug
                ? { duration: 0.45, ease: [0.4, 0, 1, 1], times: [0, 0.3, 1] }
                : { duration: 0.2 }
              }
            >
              <Link
                href={sourceUrl(`/event/${event.slug}`, source)}
                className="group block bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-2.5 hover:border-gray-300 dark:hover:border-[#25252B] transition-colors"
              >
                <div className="flex items-start gap-2 mb-1.5">
                  {event.image && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={event.image}
                      alt=""
                      className="w-8 h-8 rounded-[5px] object-cover flex-shrink-0"
                    />
                  )}
                  <p className="text-[11px] font-inter-tight font-bold text-gray-900 dark:text-white line-clamp-2 leading-tight flex-1 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                    {event.title}
                  </p>
                  <div className="relative flex items-center flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const cardEl = (e.currentTarget as HTMLElement).closest('[class*="rounded-[5px]"]');
                        if (cardEl) {
                          const rect = cardEl.getBoundingClientRect();
                          setMiniCardExplosion({ slug: event.slug, rect });
                          setTimeout(() => {
                            toggleWatchlist(event.slug);
                            setMiniCardExplosion(null);
                          }, 500);
                        } else {
                          toggleWatchlist(event.slug);
                        }
                      }}
                      onMouseEnter={() => setBookmarkHoveredSlug(event.slug)}
                      onMouseLeave={() => setBookmarkHoveredSlug(null)}
                      className="flex items-center justify-center w-6 h-6 rounded-[5px] hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                      aria-label="Remove bookmark"
                    >
                      {bookmarkHoveredSlug === event.slug ? (
                        <BookmarkXIcon size={14} isHovered={true} className="text-red-500" />
                      ) : (
                        <BookmarkIcon size={14} isHovered={false} isBookmarked={true} fillColor="#3B82F6" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-inter-tight font-bold text-gray-900 dark:text-white">
                    {(yesPrice * 100).toFixed(0)}%
                  </span>
                  {isLive && (
                    <span className="flex items-center gap-0.5 text-[9px] font-bold text-emerald-500 uppercase">
                      <span className="w-1 h-1 rounded-[5px] bg-emerald-500 animate-pulse" />
                      Live
                    </span>
                  )}
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 font-satoshi">
                    {volLabel}
                  </span>
                  {marketsCount > 1 && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 font-satoshi">
                      +{marketsCount} mkts
                    </span>
                  )}
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
      {miniCardExplosion && (
        <ExplosionEffect rect={miniCardExplosion.rect} onComplete={() => setMiniCardExplosion(null)} />
      )}
    </div>
  );
}
